import { motion } from "framer-motion";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const Roadmap = () => {
    const { lang } = useLanguage();
    const c = content[lang].roadmap;

    return (
        <section
            id="roadmap"
            data-testid="roadmap-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-blue-600/[0.06] blur-[120px]" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-400">
                        <span className="mr-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                        {c.label}
                    </span>
                    <h2
                        data-testid="roadmap-title"
                        className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                    >
                        {c.title}
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        {c.intro}
                    </p>
                </motion.div>

                <div className="mt-16 relative">
                    <div
                        aria-hidden="true"
                        className="absolute left-4 sm:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-cyan-500/50 via-cyan-500/15 dark:from-cyan-400/40 dark:via-cyan-400/15 to-transparent sm:-translate-x-1/2"
                    />

                    <ol className="space-y-10 sm:space-y-14">
                        {c.stages.map((stage, i) => {
                            const isLeft = i % 2 === 0;
                            return (
                                <motion.li
                                    key={stage.phase}
                                    data-testid={`roadmap-stage-${i + 1}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.6,
                                        ease: [0.22, 1, 0.36, 1],
                                        delay: i * 0.06,
                                    }}
                                    className="relative pl-12 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-12"
                                >
                                    <span
                                        aria-hidden="true"
                                        className="absolute left-4 sm:left-1/2 top-2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-cyan-500 dark:bg-cyan-400 shadow-[0_0_16px_rgba(6,182,212,0.6)] dark:shadow-[0_0_16px_rgba(6,182,212,0.8)] ring-4 ring-white dark:ring-[#030712]"
                                    />

                                    <div
                                        className={`glass-card p-6 sm:p-7 ${
                                            isLeft
                                                ? "sm:col-start-1 sm:text-right sm:mr-8"
                                                : "sm:col-start-2 sm:ml-8"
                                        }`}
                                    >
                                        <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">
                                            {stage.phase}
                                        </span>
                                        <h3 className="mt-2 font-display text-xl font-semibold text-slate-900 dark:text-white leading-snug">
                                            {stage.title}
                                        </h3>
                                        <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                                            {stage.description}
                                        </p>
                                    </div>
                                </motion.li>
                            );
                        })}
                    </ol>
                </div>
            </div>
        </section>
    );
};

export default Roadmap;
